import React, { useState, useEffect, useContext, useRef } from "react";
import { ThemeContext, ThemeContextData } from "../contexts/ThemeContext";
import { FaTimes } from "react-icons/fa";
import { subscribeToNewsletter } from "../utils/api";

function NewsletterPopover() {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const themeContext = useContext(ThemeContext);
  const theme = ThemeContextData[themeContext];
  const emailInputRef = useRef(null);

  // Check if user has previously closed the popover
  useEffect(() => {
    const hasClosedPopover = localStorage.getItem("hasClosedNewsletterPopover");
    
    if (!hasClosedPopover) {
      // Show popover after 5 seconds of page load
      const timer = setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);
  
  // Set focus to email input field when popover appears
  useEffect(() => {
    if (isVisible && emailInputRef.current) {
      setTimeout(() => {
        emailInputRef.current.focus();
      }, 100);
    }
  }, [isVisible]);
  
  // Add escape key handler
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isVisible) {
        handleClose();
      }
    };
    
    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isVisible]);

  // Subscribe to newsletter function
  const handleSubscribe = async () => {
    if (!fullName.trim()) {
      setMessage({ type: 'error', text: 'Please provide your full name' });
      return;
    }
    
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please provide an email address' });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    try {
      const response = await subscribeToNewsletter({ 
        fullName: fullName.trim(),
        email: email.trim() 
      });
      setLoading(false);
      setMessage({ type: 'success', text: response.message || 'Subscribed successfully!' });
      
      // Clear form and close popover after 2 seconds
      setTimeout(() => {
        setFullName('');
        setEmail('');
        handleClose();
      }, 2000);
    } catch (error) {
      setLoading(false);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to subscribe. Please try again later.'
      });
    }
  }

  // Close popover and save to localStorage
  const handleClose = () => {
    setIsAnimating(false);
    // Wait for the animation to finish before hiding the component
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
    localStorage.setItem("hasClosedNewsletterPopover", "true");
  }

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={handleClose}
      ></div>
      
      {/* Popover content */}
      <div className={`${theme.backgroundSecondary} relative w-full max-w-md mx-4 p-6 rounded-lg shadow-lg transform transition-all duration-300 ease-in-out ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        {/* Close button */}
        <button 
          onClick={handleClose}
          className={`absolute top-3 right-3 ${theme.textNormal} hover:${theme.textNormalHover}`}
          aria-label="Close"
        >
          <FaTimes className="w-5 h-5" />
        </button>
        
        {/* Content */}
        <div className="text-center mb-5">
          <h2 className={`text-2xl font-bold mb-3 ${theme.textFocus}`}>Subscribe to our Newsletter</h2>
          <p className={`text-lg font-semibold ${theme.textNormal}`}>Stay updated with our latest programs, workshops, and fitness tips!</p>
        </div>
        
        {/* Form */}
        <div className="flex flex-col space-y-4">
          <div>
            <label htmlFor="popover-fullname" className={`leading-7 text-sm font-semibold ${theme.textNormal}`}>Full Name</label>
            <input 
              type="text" 
              id="popover-fullname" 
              name="popover-fullname" 
              className={`w-full rounded border border-gray-500 ${theme.backgroundTertiary} ${theme.textFocus} focus:ring-2 focus:border-blue-500 text-base outline-none py-2 px-3 leading-8 transition-colors duration-200 ease-in-out`}
              value={fullName} 
              onChange={e => setFullName(e.target.value)}
              placeholder="John Doe"
            />
          </div>
          <div>
            <label htmlFor="popover-email" className={`leading-7 text-sm font-semibold ${theme.textNormal}`}>Email Address</label>
            <input 
              type="email" 
              id="popover-email" 
              name="popover-email" 
              className={`w-full rounded border border-gray-500 ${theme.backgroundTertiary} ${theme.textFocus} focus:ring-2 focus:border-blue-500 text-base outline-none py-2 px-3 leading-8 transition-colors duration-200 ease-in-out`}
              value={email} 
              onChange={e => setEmail(e.target.value)}
              ref={emailInputRef}
              placeholder="your.email@example.com"
            />
          </div>
          {message && (
            <div className={`mt-2 p-2 rounded ${message.type === 'success' ? 'bg-brand-500 text-white' : 'bg-brand-500 text-white'}`}>
              {message.text}
            </div>
          )}
          <button 
            className={`w-full inline-flex justify-center bg-brand-500 border-0 py-3 px-6 focus:outline-none rounded transition duration-300 font-bold text-white text-lg ${loading ? 'opacity-75 cursor-not-allowed' : 'hover:bg-brand-600'}`}
            onClick={handleSubscribe}
            disabled={loading}
          >
            {loading ? 'Subscribing...' : 'Subscribe'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewsletterPopover;
